import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { ArrowLeft, Building2, KeyRound, Info } from 'lucide-react';

const BRAND_LOGO_LIGHT = "/benchmark-ph-light.png";
const BRAND_LOGO_DARK = "/benchmark-ph-dark.png";

export default function AuthPage() {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ company_code: '', invite_code: '' });

  const { loginMember } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await loginMember(formData.company_code, formData.invite_code);
      toast.success('Welcome!');
      navigate('/questionnaire');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Invalid company code or invite code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Panel - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 md:px-12 lg:px-20 py-12">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-slate-500 hover:text-primary mb-8 transition-colors"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <div className="max-w-md">
          <img 
            src={BRAND_LOGO_LIGHT} 
            alt="Benchmark PH — IT-BPM ESG Pulse Navigator" 
            className="h-14 object-contain mb-8"
          />

          <h1 className="text-3xl font-bold text-primary mb-2">Member Sign In</h1>
          <p className="text-slate-500 mb-6">
            Sign in with the Company Code and Invite Code provided by IBPAP
          </p>

          <div className="flex items-start gap-3 p-4 bg-accent/10 border border-accent/30 rounded-xl mb-8" data-testid="invite-only-notice">
            <Info className="w-5 h-5 text-accent-dark flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-600">
              Participation is <span className="font-semibold">by invitation only</span>. If your company
              has not received its Company Code and Invite Code, please contact the IBPAP ESG Program Team.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Company Code
              </label>
              <div className="relative">
                <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  value={formData.company_code}
                  onChange={(e) => setFormData({ ...formData, company_code: e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 5) })}
                  className="w-full h-12 pl-12 pr-4 rounded-xl border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all font-mono tracking-widest uppercase"
                  placeholder="ABC12"
                  data-testid="company-code-input"
                  required
                />
              </div>
              <p className="text-xs text-slate-400 mt-2">5-character code, e.g. ACM42</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Invite Code
              </label>
              <div className="relative">
                <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  value={formData.invite_code}
                  onChange={(e) => setFormData({ ...formData, invite_code: e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 6) })}
                  className="w-full h-12 pl-12 pr-4 rounded-xl border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all font-mono tracking-widest uppercase"
                  placeholder="ABC123"
                  data-testid="invite-code-input"
                  required
                />
              </div>
              <p className="text-xs text-slate-400 mt-2">6-character code, e.g. K7M2P9</p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-accent h-12 flex items-center justify-center"
              data-testid="submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin"></div>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <p className="text-xs text-slate-400 text-center mt-4 leading-relaxed" data-testid="terms-disclaimer">
            By signing in, you agree to the{' '}
            <a href="/terms-of-use" target="_blank" rel="noopener noreferrer" className="text-accent-dark hover:underline font-medium" data-testid="terms-link">
              Terms of Use
            </a>{' '}
            &{' '}
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-accent-dark hover:underline font-medium" data-testid="privacy-link">
              Privacy Policy
            </a>.
          </p>
        </div>
      </div>

      {/* Right Panel - Visual */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-12 bg-brand-navy">
        <div className="text-center max-w-md">
          <img 
            src={BRAND_LOGO_DARK} 
            alt="Benchmark PH — IT-BPM ESG Pulse Navigator" 
            className="h-20 object-contain mx-auto mb-8"
          />
          <h2 className="text-3xl font-bold text-white mb-4">
            Shape the Future of Sustainable IT-BPM
          </h2>
          <p className="text-slate-300 leading-relaxed">
            Join industry leaders in building comprehensive ESG benchmarks. 
            Your data contributes to meaningful sustainability standards 
            for the Philippine IT-BPM sector.
          </p>
          <div className="flex justify-center gap-4 mt-8">
            <div className="bg-white/10 px-4 py-2 rounded-lg border border-white/10">
              <span className="text-accent font-semibold">E</span>
              <span className="text-white text-sm ml-1">nvironmental</span>
            </div>
            <div className="bg-white/10 px-4 py-2 rounded-lg border border-white/10">
              <span className="text-accent font-semibold">S</span>
              <span className="text-white text-sm ml-1">ocial</span>
            </div>
            <div className="bg-white/10 px-4 py-2 rounded-lg border border-white/10">
              <span className="text-accent font-semibold">G</span>
              <span className="text-white text-sm ml-1">overnance</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
